import { BillToCompanyMappingEntity, EMPTY_BILL_TO_COMPANY_MAPPING_ENTITY } from "domain/entity/BillToCompanyMapping/BillToCompanyMappingEntity";
import { ResponseEntity } from "domain/entity/Common/ResponseEntity";
import { MasterDataType } from "domain/entity/MasterData/MasterDataEntity";
import { BillToCompanyMappingRepository } from "domain/repository/BillToCompanyMapping/BillToCompanyMappingRepo";
import { ConsortiumRepository } from "domain/repository/ChargeDetailMaintenance/ConsortiumRepo";
import { ChargeTypeRepository } from "domain/repository/ChargeType/ChargeTypeRepo";
import { CompanyRepository } from "domain/repository/Company/CompanyRepo";
import { MasterDataRepository } from "domain/repository/MasterData/MasterDataRepo";
import { OpsLineRepository } from "domain/repository/OpsLine/OpsLineRepo";
import { VesselTypeRepository } from "domain/repository/VesselType/VesselTypeRepo";
import _ from "lodash";
import { createBillToCompanyMappingValidationSchema } from "presentation/constant/BillToCompanyMapping/BillToCompanyMappingValidationSchema";
import { Validation } from "presentation/constant/Validation";
import { BillToCompanyMappingModel } from "presentation/model/BillToCompanyMapping/BillToCompanyMappingModel";
import { DropdownProps } from "presentation/model/DropdownProps";
import { Dispatch, SetStateAction } from "react";
import { IFieldValue } from "veronica-ui-component/dist/component/core";
import BaseViewModel from "../BaseViewModel";

interface BillToCompanyMappingVMProps extends BaseViewModel {
    dispatch: [
        Dispatch<SetStateAction<BillToCompanyMappingModel>> | ((value: SetStateAction<BillToCompanyMappingModel>) => void),
    ],
    billToCompanyMappingRepo: BillToCompanyMappingRepository,
    chargeTypeRepo: ChargeTypeRepository,
    consortiumRepo: ConsortiumRepository,
    opsLineRepo: OpsLineRepository,
    masterDataRepo: MasterDataRepository,
    companyRepo: CompanyRepository,
    vesselTypeRepo: VesselTypeRepository,
}

export const BillToCompanyMappingVM = ({ dispatch, billToCompanyMappingRepo, chargeTypeRepo,
    consortiumRepo, opsLineRepo, masterDataRepo, companyRepo, vesselTypeRepo
}: BillToCompanyMappingVMProps) => {
    const [billToCompanyMappingDispatch] = dispatch;

    const loadDropdownOption = async () => {

        const [chargeTypes, opsLines, companyCodeList, operatingTmls, chargeCodes,
            forwarderCodes, serviceCodes, soas, dummyCompanies, consortiums, vesselTypeList] = await Promise.all([
                chargeTypeRepo.getAllChargeTypesForCombobox(),
                opsLineRepo.getOpsLineForCombobox(),
                companyRepo.getAllCompanyForCombobox(),
                masterDataRepo.getMasterDataByKey(MasterDataType.TERMINAL),
                masterDataRepo.getMasterDataByKey(MasterDataType.HANDLING_CODE),
                masterDataRepo.getMasterDataByKey(MasterDataType.FORWARDER_CODE),
                masterDataRepo.getMasterDataByKey(MasterDataType.SERVICE),
                masterDataRepo.getMasterDataByKey(MasterDataType.SOA),
                masterDataRepo.getMasterDataByKey(MasterDataType.DUMMY_CHG_ON_BILL_TO),
                consortiumRepo.getAllConsortiums(),
                vesselTypeRepo.getVesselTypeForComboBox(),
            ]);

        let newChargeTypeList = _.orderBy(chargeTypes, ["chargeType", "subChargeType"]);
        let newChargeTypes = newChargeTypeList.filter(entity => entity.chargeCategory === 'GEN' || entity.chargeCategory === 'SYS');
        let chargeTypeDropdownOption: DropdownProps[] = [];
        let subChargeTypeDropdownOption: { [key: string]: DropdownProps[] } = {};
        newChargeTypes?.forEach(chgTypeEty => {
            const chgTypeExisted = chargeTypeDropdownOption?.find(chgType =>
                chgType.value === chgTypeEty.chargeType);
            if (!chgTypeExisted) {
                chargeTypeDropdownOption.push({
                    dropdownLabel: chgTypeEty.chargeType,
                    tagLabel: chgTypeEty.chargeType,
                    value: chgTypeEty.chargeType
                })
            }
            if (chgTypeEty.subChargeType) {
                if (!subChargeTypeDropdownOption[chgTypeEty.chargeType]) {
                    subChargeTypeDropdownOption[chgTypeEty.chargeType] = [];
                }
                subChargeTypeDropdownOption[chgTypeEty.chargeType].push({
                    dropdownLabel: chgTypeEty.subChargeType,
                    tagLabel: chgTypeEty.subChargeType,
                    value: chgTypeEty.subChargeType
                });
            }
        });

        let opsLineDropdownOption: DropdownProps[] = [];
        opsLines?.forEach((opsLineEty) => {
            const opsExisted = opsLineDropdownOption?.find(opsLine =>
                opsLine.value === opsLineEty.opsLine);
            if (!opsExisted) {
                opsLineDropdownOption.push({
                    dropdownLabel: opsLineEty.opsLine,
                    tagLabel: opsLineEty.opsLine,
                    value: opsLineEty.opsLine
                })
            }
        })

        const activeCompanyCode = companyCodeList?.filter((compCode) => {
            return compCode.companyType !== 'MASTER' && compCode.companyType !== 'ALLIANCE' && compCode.status === 'CONFIRMED' && compCode.activeInd === 'Y';
        });
        // const opsLineCompanyDropdownOptions = activeCompanyCode?.map((companyEty) => ({
        //     dropdownLabel: companyEty.companyCode,
        //     tagLabel: companyEty.companyCode,
        //     value: companyEty.companyCode,
        // })) ?? [];

        let dummyCompanyCodeDropdownOptions = dummyCompanies?.map((dummyCompany) => ({
            dropdownLabel: dummyCompany.code,
            tagLabel: dummyCompany.code,
            value: dummyCompany.code,
        })) ?? []

        let companyCodeDropdownOptions = activeCompanyCode?.map((company) => ({
            dropdownLabel: company.companyCode,
            tagLabel: company.companyCode,
            value: company.companyCode,
        })) ?? []
        companyCodeDropdownOptions = _.orderBy(companyCodeDropdownOptions, "dropdownLabel");

        const operatingTmlDropdownOptions = operatingTmls?.map((operatingTml) => ({
            dropdownLabel: operatingTml.code,
            tagLabel: operatingTml.code,
            value: operatingTml.code,
        })) ?? [];

        const chargeCodeDropdownOptions = chargeCodes?.map((chargeCode) => ({
            dropdownLabel: chargeCode.code,
            tagLabel: chargeCode.code,
            value: chargeCode.code,
        })) ?? [];

        const forwarderCodesDropdownOptions = forwarderCodes?.map((forwarderCode) => ({
            dropdownLabel: forwarderCode.code,
            tagLabel: forwarderCode.code,
            value: forwarderCode.code,
        })) ?? [];

        let consortiumCodeDropdownOptions = consortiums?.map((item) => ({
            dropdownLabel: item.consortiumCode,
            tagLabel: item.consortiumCode,
            value: item.consortiumCode,
        })) ?? []
        consortiumCodeDropdownOptions = _.orderBy(consortiumCodeDropdownOptions, "dropdownLabel");

        const serviceCodeDropdownOptions = serviceCodes?.map((serviceCode) => ({
            dropdownLabel: serviceCode.code,
            tagLabel: serviceCode.code,
            value: serviceCode.code,
        })) ?? [];

        const soaCodeDropdownOptions = soas?.map((soa) => ({
            dropdownLabel: soa.code,
            tagLabel: soa.code,
            value: soa.code,
        })) ?? [];

        const vesselTypeDropdownOption = vesselTypeList?.map((vesselType) => ({
            dropdownLabel: vesselType.vesselType,
            tagLabel: vesselType.vesselType,
            value: vesselType.vesselType,
        })) ?? [];

        billToCompanyMappingDispatch(prevState => ({
            ...prevState,
            dynamicOptions: {
                ...prevState.dynamicOptions,
                chargeTypeDropdownOptions: chargeTypeDropdownOption,
                subChargeTypeDropdownOptions: subChargeTypeDropdownOption,
                // opsLineCompanyDropdownOptions: opsLineCompanyDropdownOptions,
                billToCompanyDropdownOptions: [...dummyCompanyCodeDropdownOptions, ...companyCodeDropdownOptions],
                operatingTmlDropdownOptions: operatingTmlDropdownOptions,
                chargeCodeDropdownOptions: chargeCodeDropdownOptions,
                forwarderCodeDropdownOptions: forwarderCodesDropdownOptions,
                consortiumCodeDropdownOptions: consortiumCodeDropdownOptions,
                serviceCodeDropdownOptions: serviceCodeDropdownOptions,
                soaDropdownOptions: soaCodeDropdownOptions,
                vesselTypeDropdownOptions: vesselTypeDropdownOption,
                opsLineDropdownOptions: opsLineDropdownOption,
            }
        }))
    }

    const searchAllBillToCompanyMappingList = async () => {
        billToCompanyMappingDispatch(prevState => {
            return {
                ...prevState,
                currentRow: { ...EMPTY_BILL_TO_COMPANY_MAPPING_ENTITY },
                isShowEditPanel: false,
                isAdd: false,
                isEdit: false,
                selectedRows: [],
                billToCompanyMappingList: [],
            }
        })
        await billToCompanyMappingRepo.searchAllBillToCompanyMappingList().then(data => {
            if (data) {
                // const newData = data.map(entity => ({
                //     ...entity,
                //     billToCompanyCode: ChargeOnBillToCompanyPolicy().getChargeOnBillToCompanyKeyByValue(entity.billToCompanyCode as string),
                //     alterableBillToCompany: ChargeOnBillToCompanyPolicy().getChargeOnBillToCompanyKeyByValue(entity.alterableBillToCompany as string),
                // }));
                billToCompanyMappingDispatch(prevState => {
                    return {
                        ...prevState,
                        billToCompanyMappingList: data,
                        selectedRows: [],
                    }
                })
            } else {
                billToCompanyMappingDispatch(prevState => {
                    return {
                        ...prevState,
                        billToCompanyMappingList: [],
                    }
                })
            }
        })
    }

    // const onRowDrag = (rows: BillToCompanyMappingEntity[]) => {
    //     billToCompanyMappingDispatch(prevState =>{ 
    //         const prevRows = prevState.billToCompanyMappingList;
    //         const toChangedRows = rows.map((e, index) => ({...e,tempPriority: prevRows[index].priority})).filter(e=>e.tempPriority!==e.priority);
    //         return ({
    //             ...prevState,
    //             billToCompanyMappingList: rows,
    //             updatedRows: toChangedRows,
    //     })})
    // }

    const onApply = async (updatedRows: BillToCompanyMappingEntity[]) => {

        return await billToCompanyMappingRepo.onApply(updatedRows);
    }

    const updateSelectedRows = async (rows: BillToCompanyMappingEntity[]) => {

        billToCompanyMappingDispatch(prevState => {
            return {
                ...prevState,
                selectedRows: rows,
                forceRefresh: !prevState.forceRefresh
            }
        })
    }

    const onHeaderFieldChange = async (fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) => {
        let val: any = fieldValue;

        if (fieldKey === 'chargeType') {
            billToCompanyMappingDispatch(prevState => {
                return {
                    ...prevState,
                    currentEditRow: {
                        ...prevState.currentEditRow,
                        subChargeType: val === prevState.currentEditRow.chargeType ? prevState.currentEditRow.subChargeType : ''
                    },
                }
            })
        }
        billToCompanyMappingDispatch(prevState => {
            return {
                ...prevState,
                currentEditRow: {
                    ...prevState.currentEditRow,
                    [fieldKey]: val,
                },
                masterState: {
                    ...prevState.masterState,
                    allFormState: {
                        ...prevState.masterState.allFormState,
                        [fieldKey]: ''
                    },
                }
            }
        })
    }

    const onRowDoubleClick = async (entity: BillToCompanyMappingEntity) => {
        billToCompanyMappingDispatch(prevState => {
            return {
                ...prevState,
                currentEditRow: entity,
                currentRow: entity,
                isShowEditPanel: true,
                masterState: {
                    ...prevState.masterState,
                    allFormState: {},
                }
            }
        })
    }

    const onCloseEidtPanel = () => {
        billToCompanyMappingDispatch(prevState => {
            return {
                ...prevState,
                currentEditRow: { ...EMPTY_BILL_TO_COMPANY_MAPPING_ENTITY },
                isShowEditPanel: false,
                isAdd: false,
                isEdit: false,
                masterState: {
                    ...prevState.masterState,
                    allFormState: {}
                }
            }
        })
    }

    const onSave = async (currentRow: BillToCompanyMappingEntity) => {
        const valResult = await Validation(createBillToCompanyMappingValidationSchema).ValidateFormOnly(currentRow);
        let validatedResult: { [x: string]: string } = {};
        if (valResult) {
            validatedResult = { ...valResult };

            billToCompanyMappingDispatch(prevState => {
                return {
                    ...prevState,
                    masterState: {
                        ...prevState.masterState,
                        allFormState: {
                            ...validatedResult
                        },
                    }
                }
            });

            const res: ResponseEntity = {
                code: "",
                success: false,
                msg: null,
                data: null
            }

            return res;
        }
        return billToCompanyMappingRepo.onSave(currentRow);
    }

    const onAddClick = async () => {
        billToCompanyMappingDispatch(prevState => {
            const billToCompanyCode = prevState.dynamicOptions.billToCompanyDropdownOptions[0]?.value;
            return {
                ...prevState,
                currentEditRow: { ...EMPTY_BILL_TO_COMPANY_MAPPING_ENTITY, billToCompanyCode: billToCompanyCode },
                isShowEditPanel: true,
                isAdd: true,
                masterState: {
                    ...prevState.masterState,
                    allFormState: {},
                }
            }
        })
    }
    const onResetClick = async (isAdd: boolean) => {
        billToCompanyMappingDispatch(prevState => {
            return {
                ...prevState,
                currentEditRow: isAdd ? { ...EMPTY_BILL_TO_COMPANY_MAPPING_ENTITY } : prevState.currentRow,
            }
        })
    }
    const onEditClick = async () => {
        billToCompanyMappingDispatch(prevState => {
            return {
                ...prevState,
                isEdit: true,
            }
        })
    }

    const onCheckboxChange = (checked: boolean, fieldName: string) => {
        billToCompanyMappingDispatch(prevState => {
            return {
                ...prevState,
                currentEditRow: {
                    ...prevState.currentEditRow,
                    [fieldName]: checked ? "Y" : "N",
                },
            }
        })
    }

    const onSaveClicked = () => {
        billToCompanyMappingDispatch(prevState => {
            return {
                ...prevState,
                masterState: {
                    ...prevState.masterState,
                    isSaveClicked: true,
                    allFormState: {},
                }
            }
        })
    }

    return {
        onCheckboxChange: onCheckboxChange,
        onEditClick: onEditClick,
        onResetClick: onResetClick,
        onAddClick: onAddClick,
        onSave: onSave,
        onCloseEidtPanel: onCloseEidtPanel,
        onRowDoubleClick: onRowDoubleClick,
        onHeaderFieldChange: onHeaderFieldChange,
        updateSelectedRows: updateSelectedRows,
        onApply: onApply,
        onSaveClicked: onSaveClicked,
        loadDropdownOption: loadDropdownOption,
        searchAllBillToCompanyMappingList: searchAllBillToCompanyMappingList,
    }
}